import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useClientSnapshot, useSyncClient } from '@/hooks/useClients';
import { StatusPill } from '@/components/StatusPill';
import { formatCurrency } from '@/lib/utils';

export function ClientDetailPage() {
  const { server, db } = useParams<{ server: string; db: string }>();
  const snap = useClientSnapshot(server ?? null, db ?? null);
  const sync = useSyncClient();

  return (
    <div className="space-y-6">
      <div>
        <Link to="/clientes" className="inline-flex items-center gap-1 text-xs text-moss-300/70 hover:text-mustard-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Volver al listado
        </Link>
      </div>

      {snap.isLoading && <div className="text-sm text-moss-300/70">Cargando snapshot…</div>}
      {snap.isError && (
        <div className="glass-card border-danger/30 p-6 text-sm text-danger">
          No se pudo obtener el snapshot. {(snap.error as Error).message}
        </div>
      )}
      {snap.data && (
        <>
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-moss-200">{snap.data.system_title}</h1>
              <p className="mt-1 text-sm text-moss-300/70">
                {db} · y{snap.data.year} · días de gracia config: {snap.data.alargar_dias}
              </p>
            </div>
            <button
              className="btn-ghost"
              onClick={() => server && db && sync.mutate({ serverId: server, dbName: db })}
              disabled={sync.isPending}
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
              Refrescar cache
            </button>
          </div>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Card label="Estado">
              <StatusPill value={snap.data.summary.estado} />
            </Card>
            <Card label="Adeudado" value={formatCurrency(snap.data.summary.total_adeudado)} />
            <Card label="Días vencidos" value={`${snap.data.summary.dias_vencidos}d`} />
            <Card label="Gracia restante" value={`${snap.data.summary.dias_gracia_restantes}d`} />
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="glass-card p-5">
              <h3 className="text-sm font-semibold text-moss-200">Status admin</h3>
              <Stat row="Activos" v={snap.data.admin_status.active} />
              <Stat row="Bloqueados" v={snap.data.admin_status.blocked} />
              <Stat row="Otros" v={snap.data.admin_status.other} />
            </div>
            <div className="glass-card p-5">
              <h3 className="text-sm font-semibold text-moss-200">Status teacher</h3>
              <Stat row="Activos" v={snap.data.teacher_status.active} />
              <Stat row="Bloqueados" v={snap.data.teacher_status.blocked} />
              <Stat row="Otros" v={snap.data.teacher_status.other} />
            </div>
          </section>

          <section className="glass-card overflow-hidden">
            <h3 className="border-b border-ink-700/70 px-5 py-4 text-sm font-semibold text-moss-200">
              Histórico de deudas
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-800/40 text-left text-xs uppercase tracking-wide text-moss-300/70">
                  <tr>
                    <th className="px-4 py-2">deuda_id</th>
                    <th className="px-4 py-2">mes_id</th>
                    <th className="px-4 py-2">Año</th>
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2 text-right">Cantidad</th>
                    <th className="px-4 py-2">Pagado</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.data.deudas.map((d) => (
                    <tr key={d.deuda_id} className="border-b border-ink-700/40 last:border-0">
                      <td className="px-4 py-2 text-moss-300">{d.deuda_id}</td>
                      <td className="px-4 py-2 text-moss-300">{d.mes_id}</td>
                      <td className="px-4 py-2 text-moss-300">{d.year}</td>
                      <td className="px-4 py-2 text-moss-300">{d.fecha}</td>
                      <td className="px-4 py-2 text-right font-mono text-moss-200">
                        {formatCurrency(Number(d.cantidad))}
                      </td>
                      <td className="px-4 py-2">
                        {Number(d.pagado) > 0 ? (
                          <span className="pill-ok">Pagado</span>
                        ) : (
                          <span className="pill-warn">Pendiente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {snap.data.deudas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-moss-300/60">
                        Sin deudas registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Card({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div className="glass-card p-4">
      <div className="text-xs uppercase tracking-wide text-moss-300/70">{label}</div>
      <div className="mt-2 text-xl font-semibold text-moss-200">{children ?? value}</div>
    </div>
  );
}

function Stat({ row, v }: { row: string; v: number }) {
  return (
    <div className="mt-2 flex items-center justify-between text-sm">
      <span className="text-moss-300">{row}</span>
      <span className="font-mono text-moss-200">{v}</span>
    </div>
  );
}
